import { makeAuthenticatedRequest } from './authService';
import { logApiConfig } from '../config/api';

// Log API configuration in development
if (process.env.NODE_ENV === 'development') {
  logApiConfig();
}

// Interfaces based on the API documentation
export interface Habit {
  id: number;
  user_id: number;
  name: string;
  icon: string;
  category: string;
  frequency_type: string;
  frequency_value: number;
  frequency_unit: string;
  tracking_type: string;
  target_count?: number;
  target_measurement?: number;
  measurement_unit?: string;
  is_active: boolean;
  start_date: string;
  end_date?: string;
  description?: string;
  created_at: string;
  updated_at: string;
}

export interface HabitCompletion {
  id: number;
  habit_id: number;
  completion_date: string;
  is_completed?: boolean;
  completion_count?: number;
  measurement_value?: number;
  notes?: string;
  target_achieved: boolean;
  achievement_percentage: number;
  habit_name?: string;
  icon?: string;
  category?: string;
  tracking_type?: string;
}

export interface CreateHabitData {
  user_id: number;
  name: string;
  icon?: string;
  category?: string;
  frequency_type?: string;
  frequency_value?: number;
  frequency_unit?: string;
  tracking_type?: string;
  target_count?: number;
  target_measurement?: number;
  measurement_unit?: string;
  start_date?: string;
  description?: string;
}

export interface UpdateHabitData {
  name?: string;
  icon?: string;
  category?: string;
  frequency_type?: string;
  frequency_value?: number;
  frequency_unit?: string;
  tracking_type?: string;
  target_count?: number;
  target_measurement?: number;
  measurement_unit?: string;
  is_active?: boolean;
  end_date?: string;
  description?: string;
}

export interface RecordCompletionData {
  habit_id: number;
  user_id: number;
  completion_date: string;
  is_completed?: boolean;
  completion_count?: number;
  measurement_value?: number;
  notes?: string;
}

export interface HabitFilters {
  category?: string;
  is_active?: boolean;
  limit?: number;
  offset?: number;
}

export interface CompletionFilters {
  user_id: number;
  start_date?: string;
  end_date?: string;
  limit?: number;
  offset?: number;
}

export interface HabitStats {
  period: {
    start_date: string;
    end_date: string;
    days: number;
  };
  overall: {
    total_habits: number;
    total_achieved_days: number;
    total_possible_days: number;
    success_rate: number;
    avg_achievement: number;
  };
  habits: Array<{
    habit_id: number;
    habit_name: string;
    icon: string;
    category: string;
    tracking_type: string;
    total_days: number;
    achieved_days: number;
    success_rate: number;
    avg_achievement: number;
    max_achievement: number;
    min_achievement: number;
    last_completed: string;
    weekly_completions: number;
    total_count: number;
    total_measurement: number;
  }>;
}

export interface DetailedHabitStats {
  period: {
    start_date: string;
    end_date: string;
    days: number;
  };
  stats: {
    total_days: number;
    achieved_days: number;
    success_rate: number;
    avg_achievement: number;
    max_achievement: number;
    min_achievement: number;
    month_change: number;
    month_score: number; // Score between 0-1 (can be interpreted as percentage)
    full_score: number; // Score between 0-1 (can be interpreted as percentage)
    total_completions: number;
  };
  completions: HabitCompletion[];
}

export interface HabitStreak {
  current_streak: number;
  longest_streak: number;
  total_completions: number;
  successful_completions: number;
  success_rate: number;
}

export interface HabitScore {
  habit_id: number;
  habit_name: string;
  score: number; // Score between 0-1 (can be interpreted as percentage)
  period: {
    start_date: string;
    end_date: string;
    days: number;
  };
  metrics: {
    actual_completions: number;
    expected_completions: number;
    completion_rate: number;
    frequency_type: string;
    frequency_value: number;
    frequency_unit: string;
  };
}

class HabitService {
  // Create a new habit
  async createHabit(habitData: CreateHabitData): Promise<{ success: boolean; id?: number; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest('/habits', {
        method: 'POST',
        body: JSON.stringify(habitData)
      });
      
      return response;
    } catch (error) {
      console.error('Error creating habit:', error);
      throw error;
    }
  }

  // Get habit by ID
  async getHabitById(habitId: number): Promise<{ success: boolean; habit?: Habit; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/habits/${habitId}`, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching habit:', error);
      throw error;
    }
  }

  // Get all habits for a user
  async getUserHabits(userId: number, filters?: HabitFilters): Promise<{ success: boolean; habits?: Habit[]; count?: number; message?: string }> {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (filters?.category) queryParams.append('category', filters.category);
      if (filters?.is_active !== undefined) queryParams.append('is_active', filters.is_active.toString());
      if (filters?.limit) queryParams.append('limit', filters.limit.toString());
      if (filters?.offset) queryParams.append('offset', filters.offset.toString());
      
      const queryString = queryParams.toString();
      const url = `/habits/user/${userId}${queryString ? `?${queryString}` : ''}`;
      
      const response = await makeAuthenticatedRequest(url, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching user habits:', error);
      throw error;
    }
  }

  // Update a habit
  async updateHabit(habitId: number, updateData: UpdateHabitData): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/habits/${habitId}`, {
        method: 'PUT',
        body: JSON.stringify(updateData)
      });
      
      return response;
    } catch (error) {
      console.error('Error updating habit:', error);
      throw error;
    }
  }

  // Delete a habit
  async deleteHabit(habitId: number): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/habits/${habitId}`, {
        method: 'DELETE'
      });
      
      return response;
    } catch (error) {
      console.error('Error deleting habit:', error);
      throw error;
    }
  }

  // Search habits
  async searchHabits(userId: number, searchTerm?: string, filters?: HabitFilters): Promise<{ success: boolean; habits?: Habit[]; count?: number; message?: string }> {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      queryParams.append('user_id', userId.toString());
      if (searchTerm) queryParams.append('search_term', searchTerm);
      if (filters?.category) queryParams.append('category', filters.category);
      if (filters?.is_active !== undefined) queryParams.append('is_active', filters.is_active.toString());
      if (filters?.limit) queryParams.append('limit', filters.limit.toString());
      if (filters?.offset) queryParams.append('offset', filters.offset.toString());
      
      const response = await makeAuthenticatedRequest(`/habits/search?${queryParams.toString()}`, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error searching habits:', error);
      throw error;
    }
  }

  // Record habit completion
  async recordCompletion(completionData: RecordCompletionData): Promise<{ success: boolean; message?: string; target_achieved?: boolean; achievement_percentage?: number }> {
    try {
      const response = await makeAuthenticatedRequest('/habit-completions', {
        method: 'POST',
        body: JSON.stringify(completionData)
      });
      
      return response;
    } catch (error) {
      console.error('Error recording completion:', error);
      throw error;
    }
  }

  // Get habit completions
  async getHabitCompletions(habitId: number, filters: CompletionFilters): Promise<{ success: boolean; completions?: HabitCompletion[]; count?: number; message?: string }> {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      queryParams.append('user_id', filters.user_id.toString());
      if (filters.start_date) queryParams.append('start_date', filters.start_date);
      if (filters.end_date) queryParams.append('end_date', filters.end_date);
      if (filters.limit) queryParams.append('limit', filters.limit.toString());
      if (filters.offset) queryParams.append('offset', filters.offset.toString());
      
      const response = await makeAuthenticatedRequest(`/habit-completions/habit/${habitId}?${queryParams.toString()}`, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching habit completions:', error);
      throw error;
    }
  }

  // Get daily completions for a user
  async getDailyCompletions(userId: number, date: string): Promise<{ success: boolean; date?: string; completions?: HabitCompletion[]; count?: number; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/habit-completions/user/${userId}/date/${date}`, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching daily completions:', error);
      throw error;
    }
  }

  // Get completions for a user within a date range
  async getCompletionsByDateRange(userId: number, startDate: string, endDate: string): Promise<{ 
    success: boolean; 
    start_date?: string; 
    end_date?: string; 
    completions?: HabitCompletion[]; 
    completions_by_date?: { [date: string]: HabitCompletion[] };
    count?: number; 
    message?: string 
  }> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('start_date', startDate);
      queryParams.append('end_date', endDate);
      
      const response = await makeAuthenticatedRequest(`/habit-completions/user/${userId}/range?${queryParams.toString()}`, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching completions by date range:', error);
      throw error;
    }
  }

  // Get completion statistics
  async getCompletionStats(userId: number, habitId?: number, startDate?: string, endDate?: string, days?: number): Promise<{ success: boolean; stats?: HabitStats }> {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (habitId) queryParams.append('habit_id', habitId.toString());
      if (startDate) queryParams.append('start_date', startDate);
      if (endDate) queryParams.append('end_date', endDate);
      if (days) queryParams.append('days', days.toString());
      
      const queryString = queryParams.toString();
      const url = `/habit-completions/user/${userId}/stats${queryString ? `?${queryString}` : ''}`;
      
      const response = await makeAuthenticatedRequest(url, {
        method: 'GET'
      });
      
      return { success: response.success, stats: response };
    } catch (error) {
      console.error('Error fetching completion stats:', error);
      throw error;
    }
  }

  // Get habit streak
  async getHabitStreak(habitId: number, userId: number): Promise<{ success: boolean; streak?: HabitStreak }> {
    try {
      const response = await makeAuthenticatedRequest(`/habit-completions/habit/${habitId}/streak?user_id=${userId}`, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching habit streak:', error);
      throw error;
    }
  }

  // Get habit score
  async getHabitScore(habitId: number, userId: number, options?: { days?: number; start_date?: string; end_date?: string }): Promise<{ success: boolean; score?: HabitScore; message?: string }> {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      queryParams.append('user_id', userId.toString());
      if (options?.days) queryParams.append('days', options.days.toString());
      if (options?.start_date) queryParams.append('start_date', options.start_date);
      if (options?.end_date) queryParams.append('end_date', options.end_date);
      
      const response = await makeAuthenticatedRequest(`/habit-completions/habit/${habitId}/score?${queryParams.toString()}`, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching habit score:', error);
      throw error;
    }
  }

  // Get detailed habit statistics with achievement data
  async getDetailedHabitStats(habitId: number, userId: number, days?: number): Promise<{ success: boolean; stats?: DetailedHabitStats['stats']; completions?: HabitCompletion[]; period?: DetailedHabitStats['period']; message?: string }> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('user_id', userId.toString());
      if (days) queryParams.append('days', days.toString());
      
      const response = await makeAuthenticatedRequest(`/habit-completions/habit/${habitId}/detailed-stats?${queryParams.toString()}`, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching detailed habit statistics:', error);
      throw error;
    }
  }

  // Delete completion
  async deleteCompletion(habitId: number, date: string, userId: number): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/habit-completions/${habitId}/${date}?user_id=${userId}`, {
        method: 'DELETE'
      });
      
      return response;
    } catch (error) {
      console.error('Error deleting completion:', error);
      throw error;
    }
  }

  // Helper method to format date for API
  formatDateForAPI(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  // Helper method to get current week dates
  getCurrentWeekDates(): Date[] {
    const today = new Date();
    const currentDay = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - currentDay + 1); // Start from Monday

    const weekDates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      weekDates.push(date);
    }
    return weekDates;
  }
}

export const habitService = new HabitService();
